import { Canvas } from '@react-three/fiber'
import { Suspense } from 'react';
import { BaseFigure as Model, FigureOverlay, ModelLoading } from '../..';
import { Center, CameraControls } from '@react-three/drei';
import { theme } from '../../../theme';
import { useState, useRef } from 'react';
import * as THREE from 'three'
import { parseEquipment } from '../../../utils';

const Sphere = ({ radius = 1, segments = 20 }) => {
const CURVE_POINTS = 200;
  const meridians = [];
  // Meridians
  for (let i = 0; i < segments; i++) {
    const rotation = Math.PI * (i / segments);
    const curve = new THREE.EllipseCurve(
      0,  0,            // ax, aY
      radius, radius,           // xRadius, yRadius
      0,  2 * Math.PI,  // aStartAngle, aEndAngle
      false,            // aClockwise
      0                 // aRotation
    );
    const points = curve.getPoints(CURVE_POINTS);
    meridians.push({ g: new THREE.BufferGeometry().setFromPoints(points), r: rotation });
  }
  // Parallels
  let parallels = [];
  for (let i = 0; i < segments; i++) {
    let pOffset = radius - radius / Math.pow(2, i);
    let pRadius = Math.sqrt(radius * radius - pOffset * pOffset);
    let curve = new THREE.EllipseCurve(
      0,  0,            // ax, aY
      pRadius, pRadius,           // xRadius, yRadius
      0,  2 * Math.PI,  // aStartAngle, aEndAngle
      false,            // aClockwise
      0                 // aRotation
    );
    const points = curve.getPoints(CURVE_POINTS);
    parallels.push({ g: new THREE.BufferGeometry().setFromPoints(points), p: -pOffset });
  }
  const material = new THREE.LineBasicMaterial({color: 0xffffff, transparent: true, opacity: 0.4});

  return (
    <mesh position={[0, radius - 0.01, 0]} castShadow>
      {meridians.map(({ g, r }, i) => (<line key={`mer-${i}`} rotation={[0, r, 0]} args={[g, material]} />))}
      {parallels.map(({ g, p }, i) => (<line key={`par-${i}`} position={[0, p, 0]} rotation={[Math.PI / 2, 0, 0]} args={[g, material]} />))}
    </mesh>
  );
};

const Scene = ({ controls = null, animationType, figure, noEquip = false, customEquip = null }) => {
  const sphereRadius = 24;
 
  let weapons = noEquip ? [] : [{ weaponType: 'heavy', effects: ['TWO_HANDED'], weaponGlbPath: '/assets/shotty_mint.glb' }];
  let droneGlbPath = null;
  let droneType = null;
  let objectGlbPath = noEquip ? null : '/assets/ramen_mint.glb';
  let objectType = null;
  let armorGlbPath = null;
  let companionGlbPath = null;

  if (customEquip) {
    const equipmentData = parseEquipment(customEquip, figure, true);
    objectGlbPath = equipmentData.objectPath;
    weapons = equipmentData.weapons;
    objectType = equipmentData.objectType;
    droneGlbPath = equipmentData.dronePath;
    droneType = equipmentData.droneType;
    companionGlbPath = equipmentData.companionPath;
    armorGlbPath = equipmentData.armorPath;
  } 
  return (
    <>
      <ambientLight intensity={1} />
      <Suspense fallback={
          <FigureOverlay><ModelLoading /></FigureOverlay>
        }
      >
        <Center bottom position={[0, sphereRadius * 2 - 1, 0]}>
          <Sphere radius={sphereRadius}/>
        </Center>
        <Model controls={controls} 
          animationType={animationType} 
          key={`figure-${figure.getId()}`} 
          scale={figure.getScaleFactor()} 
          glbPath={figure.getVrmSource()} 
          rotation={[0, 0, 0]} 
          position={[0, -1, 0]} 
          weapons={weapons}
          objectGlbPath={objectGlbPath}
          armorGlbPath={armorGlbPath}
          droneGlbPath={droneGlbPath}
          companionGlbPath={companionGlbPath}
          objectType={objectType}
          droneType={droneType}
        />
      </Suspense>
    </>
  );
}

const MOVEMENT_POSITIONS = {
  'center': {
    target: { x: 0, y: -0.15, z: 0 },
    position: { x: -2.75, y: 1.25, z: -7.25 }
  }  
};

export default function MintFigureView({ figure, animationType, width, height, maxHeight, controls = null, noDecorators = false, noEquip = false, customEquip = null }) {
  const [enabledControls, setEnableControls] = useState(true);  
  const [dragging, setDragging] = useState(false);
  const figureControlsRef = useRef(null);
  

  const moveSmoothly = (newPosition, notSmooth = false) => {
    if (!figureControlsRef.current) return;
    const {target, position} = newPosition;
    figureControlsRef.current.setTarget(target.x, target.y, target.z, !notSmooth);
    figureControlsRef.current.setPosition(position.x, position.y, position.z, !notSmooth);
  }

  const moveAvatarLeft = () => {
    if (figureControlsRef.current) {
      setEnableControls(true);
      moveSmoothly(MOVEMENT_POSITIONS['left']);
    }
  };

  const moveAvatarRight = () => {
    if (figureControlsRef.current) {
      setEnableControls(false);
      moveSmoothly(MOVEMENT_POSITIONS['right']);
    }
  };

  const centerAvatar = (forHome = false) => {
    if (figureControlsRef.current) {
      setEnableControls(true);
      moveSmoothly(MOVEMENT_POSITIONS[forHome ? 'home' : 'center'], forHome);
    }
  };

  const zoomToFace = () => {
    if (figureControlsRef.current) {
      setEnableControls(true);
      moveSmoothly(MOVEMENT_POSITIONS['zoom']);
    }
  }

  const selectFirstAvatar = () => {
    if (figureControlsRef.current) {
      setEnableControls(true);
      moveSmoothly(MOVEMENT_POSITIONS['figure1']);
    }
  };

  if (controls) {
    controls.moveAvatarLeft = moveAvatarLeft;
    controls.moveAvatarRight = moveAvatarRight;
    controls.centerAvatar = centerAvatar;
    controls.zoomToFace = zoomToFace;
    controls.selectFirstAvatar = selectFirstAvatar;
  }

  return (
    <div onMouseUp={() => setDragging(false)} onMouseDown={() => setDragging(true)} style={{maxHeight, height, cursor: dragging ? 'grabbing' : 'grab'}}>
      <Canvas
        gl={{antialias: true, logarithmicDepthBuffer: true}}
        shadows={true} 
        onCreated={() => setTimeout(() => centerAvatar(), 1000)} 
        style={{
          zIndex: 0,
          backgroundColor: 
          theme.lightBlue, 
          width: width,
          height: height,
          maxHeight: maxHeight,
          //gridColumn: 1,
          //gridRow: 1
        }} 
        camera={{position:[2.11, 0.68, -7.56], fov: 15 }}
      >
        <Scene animationType={animationType} 
          controls={controls} 
          figure={figure}
	  noEquip={noEquip}
          customEquip={customEquip}
        />
          <CameraControls ref={figureControlsRef}
            mouseButtons={{ left: 1, wheel: 0 }}
            dollyToCursor={true} 
            minDistance={5} 
            maxDistance={25}
            distance={5}
          />
      </Canvas>
    </div>
  );
}
